import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, Copy, Factory, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import {
  useGetAgentFactoriesQuery,
  useGetAgentModelCollectionsQuery,
  useCloneAgentFactoryMutation,
  useDeleteAgentFactoryMutation,
} from '@/store/api/agentFactoryApi';
import type { AgentFactory } from '@/types';

export function FactoriesPage() {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const { data: factories, isLoading, isError } = useGetAgentFactoriesQuery();
  const { data: collections } = useGetAgentModelCollectionsQuery();
  const collectionById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of collections ?? []) map.set(c._id, c.name);
    return map;
  }, [collections]);

  const [cloneTarget, setCloneTarget] = useState<AgentFactory | null>(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneFactory, { isLoading: cloning }] = useCloneAgentFactoryMutation();

  const [deleteTarget, setDeleteTarget] = useState<AgentFactory | null>(null);
  const [deleteFactory, { isLoading: deleting }] = useDeleteAgentFactoryMutation();

  const openClone = (f: AgentFactory) => {
    setCloneTarget(f);
    setCloneName(`${f.name}-clone`);
  };

  const handleClone = async () => {
    if (!cloneTarget) return;
    try {
      const result = await cloneFactory({ id: cloneTarget._id, name: cloneName.trim() }).unwrap();
      toastSuccess('Factory cloned');
      setCloneTarget(null);
      navigate(`/factories/${result._id}`);
    } catch (err) {
      toastError((err as any)?.data?.message || 'Failed to clone factory');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteFactory(deleteTarget._id).unwrap();
      toastSuccess('Factory deleted');
      setDeleteTarget(null);
    } catch {
      toastError('Failed to delete factory');
    }
  };

  const columns: ColumnDef<AgentFactory, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Factory className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="truncate font-medium">{row.original.name}</p>
              {row.original.description && (
                <p className="truncate text-xs text-muted-foreground">{row.original.description}</p>
              )}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'modelCollectionId',
        header: 'Model collection',
        cell: ({ row }) => (
          <span className="truncate text-sm text-muted-foreground">
            {collectionById.get(row.original.modelCollectionId) ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'policyIds',
        header: 'Policies',
        cell: ({ row }) => <span className="tabular-nums">{row.original.policyIds?.length ?? 0}</span>,
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? 'success' : 'secondary'}>
            {row.original.isActive ? 'active' : 'inactive'}
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              title="Clone"
              onClick={(e) => {
                e.stopPropagation();
                openClone(row.original);
              }}
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Open"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/factories/${row.original._id}`);
              }}
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Edit"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/factories/${row.original._id}/edit`);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(row.original);
              }}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collectionById, navigate],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Agent Factories</h1>
          <p className="text-sm text-muted-foreground">
            Reusable agent blueprints — model collection, instruction, bootstrap, compaction.
          </p>
        </div>
        <Button onClick={() => navigate('/factories/new')}>
          <Plus className="mr-2 h-4 w-4" /> New Factory
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-0">
          <p className="text-sm font-medium">Configured factories</p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : isError ? (
            <p className="py-8 text-center text-sm text-destructive">Failed to load factories.</p>
          ) : !factories || factories.length === 0 ? (
            <EmptyState
              title="No factories"
              description="Create a factory to define how agent conversations behave."
              action={<Button onClick={() => navigate('/factories/new')}>New Factory</Button>}
            />
          ) : (
            <DataTable
              columns={columns}
              data={factories}
              onRowClick={(f) => navigate(`/factories/${f._id}`)}
            />
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete factory"
        description={`Are you sure you want to delete "${deleteTarget?.name ?? 'this factory'}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleting}
      />

      {/* ── Clone dialog (AF-7) ─────────────────────────────────────────── */}
      <Dialog open={!!cloneTarget} onOpenChange={(open) => !open && setCloneTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clone factory</DialogTitle>
            <DialogDescription>
              Deep-copy "{cloneTarget?.name}" as a new factory (version resets to 1).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="clone-name">Clone name *</Label>
            <Input
              id="clone-name"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              placeholder="my-factory-clone"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneTarget(null)} disabled={cloning}>
              Cancel
            </Button>
            <Button onClick={handleClone} disabled={cloning || !cloneName.trim()}>
              {cloning ? 'Cloning…' : 'Clone'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}